import React from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";

import {
  forgotPasswordRequestSchema,
  ForgotPasswordRequestFormData,
} from "../../schemas/auth.schema";
import apiClient from "../../api/apiClient";
import Input from "../form/Input";
import Button from "../form/Button";
import { Mail } from "heroicons-react";

interface ForgotPasswordFormProps {
  onCancel: () => void;
}

export default function ForgotPasswordForm({ onCancel }: ForgotPasswordFormProps) {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ForgotPasswordRequestFormData>({
    resolver: zodResolver(forgotPasswordRequestSchema),
    defaultValues: {
      email: "",
    },
    mode: "onSubmit",
  });

  const onSubmit = async (data: ForgotPasswordRequestFormData) => {
    try {
      await apiClient.post("/api/v1/auth/email", { email: data.email });
      toast.success(
        t(
          "login.forgotEmailSent",
          "If an account exists with this email, a password reset link has been sent."
        )
      );
      reset();
      onCancel();
    } catch (err: any) {
      const errorMsg =
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message ||
        "Failed to send reset link";
      toast.error(errorMsg);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full flex flex-col gap-5">
      {/* Header */}
      <div className="text-center">
        <Link to="/" className="inline-block">
          <img
            src="./images/bluelogo-new.png"
            alt="Logo"
            className="mx-auto mb-3 max-w-[130px] md:max-w-[150px]"
          />
        </Link>

        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#EBF7F9] text-[#037897] mb-3">
          <Mail className="w-6 h-6" />
        </div>

        <h2 className="mb-2 font-gilroy text-[#263238] font-semibold text-[26px] md:text-[30px]">
          {t("login.forgotPassword", "Forgot password?")}
        </h2>

        <p className="text-gray-7 text-[#78909C] font-gilroy text-[15px] md:text-[17px]">
          {t(
            "login.forgotSubtitle",
            "Enter your email address and we'll send you a link to reset your password."
          )}
        </p>
      </div>

      {/* Email */}
      <Input
        id="forgotEmail"
        label={t("login.emailLabel", "Email Address")}
        type="email"
        placeholder={t("login.emailPlaceholder", "Enter your email")}
        autoComplete="email"
        error={errors.email?.message}
        {...register("email")}
      />

      {/* Submit Button */}
      <Button
        isLoading={isSubmitting}
        loadingText={t("common.loading", "Sending...")}
        className="!mt-2"
      >
        {t("login.sendResetLink", "Send Reset Link")}
      </Button>

      {/* Back to Login */}
      <div className="text-center pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="text-sm text-[#037897] font-semibold hover:underline"
        >
          {t("login.cancelReset", "Back to Login")}
        </button>
      </div>
    </form>
  );
}
